import React, { useState, useEffect, useCallback } from 'react';
import { transportAPI } from '../../services/api';

const StudentTransportViewTab = ({ isParentView = false, studentId = null, studentName = '' }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transportData, setTransportData] = useState(null);
  const [allRoutes, setAllRoutes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let res = null;
      if (isParentView && studentId) {
        res = await transportAPI.getParentStudentTransport(studentId);
      } else {
        res = await transportAPI.getMyTransport();
      }

      setTransportData(res.my_transport || null);
      setAllRoutes(Array.isArray(res.all_routes) ? res.all_routes : []);
    } catch (err) {
      console.error('Error loading transport details:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load transport details.');
    } finally {
      setLoading(false);
    }
  }, [isParentView, studentId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Advanced multi-criteria search filter
  const filteredRoutes = allRoutes.filter(r => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;

    const matchRouteNum = (r.route_number || '').toLowerCase().includes(q);
    const matchRouteName = (r.route_name || '').toLowerCase().includes(q);
    const matchStartPoint = (r.starting_point || '').toLowerCase().includes(q);
    const matchStops = (r.stops || []).some(s =>
      (s.destination || '').toLowerCase().includes(q) ||
      String(s.distance_km || '').includes(q)
    );
    const vehicle = r.assigned_vehicle;
    const matchVehicleReg = (vehicle?.registration_number || '').toLowerCase().includes(q);
    const matchVehicleType = (vehicle?.vehicle_type_display || vehicle?.vehicle_type || '').toLowerCase().includes(q);
    const driver = vehicle?.assigned_driver || vehicle?.driver;
    const matchDriverName = (driver?.driver_name || driver?.name || '').toLowerCase().includes(q);
    const matchDriverPhone = (driver?.phone_number || '').toLowerCase().includes(q);

    return (
      matchRouteNum ||
      matchRouteName ||
      matchStartPoint ||
      matchStops ||
      matchVehicleReg ||
      matchVehicleType ||
      matchDriverName ||
      matchDriverPhone
    );
  });

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#64748b', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        Loading transport & bus details...
      </div>
    );
  }

  const cleanPhone = (phone) => {
    if (!phone || typeof phone !== 'string') return 'N/A';
    const trimmed = phone.trim();
    if (trimmed.toLowerCase() === 'insurance' || trimmed.toLowerCase() === 'n/a' || !/\d/.test(trimmed)) {
      return 'N/A';
    }
    return trimmed;
  };

  const assignedVehicle = transportData?.vehicle_details;
  const driverInfo = assignedVehicle?.assigned_driver;
  const rawPhone = transportData?.driver_phone || driverInfo?.phone_number || driverInfo?.phone;
  const driverPhone = cleanPhone(rawPhone);

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', 'Segoe UI', sans-serif", display: 'flex', flexDirection: 'column', gap: 24 }}>
      
      {/* Header Banner */}
      <div style={{ background: '#f8fafc', padding: 20, borderRadius: 14, border: '1px solid #cbd5e1', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
            🚌 College Bus & Transport Information
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
            {isParentView
              ? `View transport assignment, assigned bus details, and driver contact numbers for ${studentName || 'Student'}.`
              : 'View your assigned bus details, driver contact numbers for inquiries, and available college transport route schedules.'}
          </p>
        </div>
        {transportData ? (
          <span className="badge badge-green" style={{ fontSize: 12, padding: '6px 14px', borderRadius: 20, fontWeight: 700 }}>
            ✓ Active Bus Facility Enrolled
          </span>
        ) : (
          <span className="badge badge-yellow" style={{ fontSize: 12, padding: '6px 14px', borderRadius: 20, fontWeight: 700 }}>
            Bus Facility Not Enrolled
          </span>
        )}
      </div>

      {error && (
        <div style={{ padding: 14, background: '#fff1f2', border: '1px solid #fecdd3', color: '#9f1239', borderRadius: 10, fontSize: 13 }}>
          ⚠️ {error}
        </div>
      )}

      {/* SECTION 1: MY ASSIGNED BUS & DRIVER DETAILS (ONLY SHOWN WHEN ASSIGNED) */}
      {transportData ? (
        <div style={{ background: '#ffffff', borderRadius: 16, border: '2px solid #3b82f6', overflow: 'hidden', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.08)' }}>
          {/* Card Header */}
          <div style={{ background: '#1e40af', color: '#ffffff', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 20 }}>🚍</span>
              <h4 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
                Assigned Bus Details — Route {transportData.route_number}
              </h4>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, background: '#3b82f6', padding: '4px 12px', borderRadius: 8 }}>
              {transportData.route_name}
            </div>
          </div>

          {/* Card Body */}
          <div style={{ padding: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
            
            {/* Bus & Stop Info */}
            <div style={{ background: '#eff6ff', padding: 16, borderRadius: 12, border: '1px solid #bfdbfe' }}>
              <h5 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700, color: '#1e40af', display: 'flex', alignItems: 'center', gap: 6 }}>
                📍 Route & Stop Details
              </h5>
              <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 6, color: '#1e293b' }}>
                <div><span style={{ color: '#64748b' }}>Route ID:</span> <strong>{transportData.route_number}</strong></div>
                <div><span style={{ color: '#64748b' }}>Assigned Stop:</span> <strong style={{ color: '#1d4ed8', fontSize: 14 }}>{transportData.destination}</strong></div>
                <div><span style={{ color: '#64748b' }}>Distance from Campus:</span> <strong>{transportData.distance_km} KM</strong></div>
                <div><span style={{ color: '#64748b' }}>Transport Fee:</span> <strong style={{ color: '#15803d', fontSize: 15 }}>₹ {Number(transportData.transport_fee).toLocaleString('en-IN')}</strong></div>
              </div>
            </div>

            {/* Vehicle Details */}
            <div style={{ background: '#f0fdf4', padding: 16, borderRadius: 12, border: '1px solid #bbf7d0' }}>
              <h5 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700, color: '#166534', display: 'flex', alignItems: 'center', gap: 6 }}>
                🚌 Vehicle / Bus Details
              </h5>
              <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 6, color: '#1e293b' }}>
                <div><span style={{ color: '#64748b' }}>Bus Registration No:</span> <strong style={{ color: '#15803d', fontSize: 15 }}>{transportData.vehicle_registration || assignedVehicle?.registration_number || 'N/A'}</strong></div>
                <div><span style={{ color: '#64748b' }}>Vehicle Type:</span> <strong>{transportData.vehicle_type_display || assignedVehicle?.vehicle_type || 'Bus'}</strong></div>
                <div><span style={{ color: '#64748b' }}>Make & Model:</span> <strong>{assignedVehicle ? `${assignedVehicle.make || ''} ${assignedVehicle.model || ''}` : 'N/A'}</strong></div>
                <div><span style={{ color: '#64748b' }}>Seating Capacity:</span> <strong>{assignedVehicle?.seating_capacity || 'N/A'} Seats</strong></div>
              </div>
            </div>

            {/* Driver & Contact for Checking / Emergency */}
            <div style={{ background: '#fff7ed', padding: 16, borderRadius: 12, border: '1px solid #ffedd5' }}>
              <h5 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700, color: '#c2410c', display: 'flex', alignItems: 'center', gap: 6 }}>
                🧑‍✈️ Driver & Emergency Contact (Inquiries)
              </h5>
              <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 6, color: '#1e293b' }}>
                <div><span style={{ color: '#64748b' }}>Driver Name:</span> <strong style={{ fontSize: 14 }}>{transportData.driver_name || driverInfo?.driver_name || 'Driver Assigned'}</strong></div>
                <div>
                  <span style={{ color: '#64748b' }}>Driver Phone Number:</span>{' '}
                  {driverPhone && driverPhone !== 'N/A' ? (
                    <a
                      href={`tel:${driverPhone}`}
                      style={{ background: '#ea580c', color: '#ffffff', padding: '4px 10px', borderRadius: 6, textDecoration: 'none', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4 }}
                    >
                      📞 {driverPhone}
                    </a>
                  ) : (
                    <span style={{ color: '#94a3b8', fontSize: 13, fontWeight: 600 }}>N/A (Not Provided)</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#9a3412', marginTop: 4 }}>
                  Contact driver directly for bus location, arrival checking, or delay inquiries.
                </div>
              </div>
            </div>

          </div>
        </div>
      ) : (
        /* SECTION 2: ALL COLLEGE BUS ROUTES DIRECTORY WITH ADVANCED SEARCH (ONLY SHOWN WHEN NOT ASSIGNED) */
        <div style={{ background: '#ffffff', borderRadius: 16, border: '1px solid #cbd5e1', padding: 20 }}>
          <div style={{ padding: 16, background: '#f8fafc', borderRadius: 12, border: '1px dashed #cbd5e1', marginBottom: 20, color: '#475569', fontSize: 13, textAlign: 'center' }}>
            ℹ️ {isParentView ? `${studentName || 'Student'} is not currently assigned to any bus route.` : 'You are not currently assigned to any bus route.'} Check available routes, bus numbers, and driver details below.
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
            <h4 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
              🗺️ All College Bus Routes & Driver Directory
            </h4>
            <div style={{ flex: 1, maxWidth: 440 }}>
              <input
                type="text"
                placeholder="🔍 Search route ID, route name, stop/destination, bus reg, or driver..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, width: '100%', outline: 'none' }}
              />
            </div>
          </div>

          {filteredRoutes.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              No college bus routes found matching "{searchQuery}".
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>Route ID</th>
                    <th>Route Name</th>
                    <th>Stops & Destinations Breakdown</th>
                    <th>Operating Bus</th>
                    <th>Driver Contact (Inquiries)</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRoutes.map(r => {
                    const vehicle = r.assigned_vehicle;
                    const driver = vehicle?.assigned_driver || vehicle?.driver;
                    const dPhone = cleanPhone(driver?.phone_number || driver?.phone);

                    return (
                      <tr key={r.id}>
                        <td>
                          <span className="badge badge-blue" style={{ fontSize: 12, fontWeight: 700 }}>
                            {r.route_number}
                          </span>
                        </td>
                        <td>
                          <strong>{r.route_name}</strong>
                          <div style={{ fontSize: 11, color: '#64748b' }}>Start: {r.starting_point || 'Campus'}</div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, fontSize: 11 }}>
                            {(r.stops || []).map((s, idx) => (
                              <span key={idx} style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', padding: '2px 6px', borderRadius: 4 }}>
                                {s.destination} ({s.distance_km} KM)
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          {vehicle ? (
                            <div>
                              <strong style={{ color: '#0f172a' }}>{vehicle.registration_number}</strong>
                              <div style={{ fontSize: 11, color: '#64748b' }}>{vehicle.vehicle_type_display} ({vehicle.seating_capacity} Seats)</div>
                            </div>
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: 12 }}>No vehicle assigned</span>
                          )}
                        </td>
                        <td>
                          {driver ? (
                            <div>
                              <strong style={{ color: '#166534' }}>🧑‍✈️ {driver.driver_name || driver.name}</strong>
                              <div style={{ marginTop: 2 }}>
                                {dPhone && dPhone !== 'N/A' ? (
                                  <a href={`tel:${dPhone}`} style={{ color: '#2563eb', fontWeight: 600, fontSize: 12, textDecoration: 'none' }}>
                                    📞 {dPhone}
                                  </a>
                                ) : (
                                  <span style={{ color: '#94a3b8', fontSize: 12 }}>📞 N/A</span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: 12 }}>Driver N/A</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default StudentTransportViewTab;
